import React, { useState } from "react";
import Modal from "react-modal";
import axios from "axios";
import { toast } from "react-toastify";

import { useLanguage } from "../../context/languageContext";
import Button from "../../components/button";
import Select from "../../components/select";
import { SegmentedToggle } from "../../components/editPageHeader";

const API = process.env.REACT_APP_API_ROOT;

// Today as the panel's calendar day, not UTC's.
const today = () => {
    const d = new Date();
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

/**
 * Marks a sick or festival leave for one employee over a date range (both
 * ends inclusive). `employeeId` preselects the person when opened from a row.
 */
const LeavePopup = ({ employeeOptions, employeeId, handleCloseModal, onSuccess }) => {
    const { adminInfo } = useLanguage();

    const [employee, setEmployee] = useState(employeeOptions.find((o) => o.value === employeeId) || null);
    const [type, setType] = useState("sick");
    const [fromDate, setFromDate] = useState(today);
    const [toDate, setToDate] = useState(today);
    const [note, setNote] = useState("");
    const [error, setError] = useState("");
    const [isSubmit, setIsSubmit] = useState(false);

    const handleFromChange = (value) => {
        setFromDate(value);
        // Moving the start past the end drags the end with it — a one-day
        // leave stays one day instead of becoming an error.
        if (value && toDate < value) setToDate(value);
    };

    const handleSubmit = async () => {
        if (!employee) return setError("Please choose an employee.");
        if (!fromDate || !toDate) return setError("Please pick the dates.");
        if (toDate < fromDate) return setError("The end date can't be before the start date.");
        setError("");

        try {
            setIsSubmit(true);
            const headers = { Authorization: `Bearer ${adminInfo.token}` };
            const payload = { employeeId: employee.value, type, fromDate, toDate, note: note.trim() };
            const { data: response } = await axios.post(`${API}/api/admin/employees/leave/add`, payload, { headers });
            if (response.status) {
                toast.success(`Leave marked for ${employee.label}.`);
                onSuccess();
                handleCloseModal();
            } else {
                toast.error(response.message || "Could not mark this leave.");
            }
        } catch (err) {
            console.error("handleSubmit catch error::", err);
            // The overlap refusal names the leave already there — show it as is.
            setError(err.response?.data?.message || err.response?.data?.error || "Could not mark this leave.");
        } finally {
            setIsSubmit(false);
        }
    };

    return (
        <Modal isOpen onRequestClose={handleCloseModal} className="modal_body_sm" overlayClassName="modal_overlay">
            <div className="box_wrapper">
                <h4 className="fw-bold mb-3">Mark Leave</h4>

                <div className="modal_content_sm">
                    <div className="mb-3">
                        <label className="label">Employee:</label>
                        <Select
                            className="form-control-md fw-medium p-0"
                            placeholder="Choose an employee..."
                            value={employee}
                            onChange={setEmployee}
                            options={employeeOptions}
                        />
                    </div>
                    <div className="mb-3">
                        <SegmentedToggle
                            label="Type"
                            value={type}
                            onChange={setType}
                            options={[
                                { value: "sick", label: "Sick" },
                                { value: "festival", label: "Festival" },
                            ]}
                        />
                    </div>
                    <div className="mb-3 d-flex gap-2">
                        <div className="flex-fill">
                            <label className="label">From:</label>
                            <input className="form_control" type="date" value={fromDate} onChange={(e) => handleFromChange(e.target.value)} />
                        </div>
                        <div className="flex-fill">
                            <label className="label">To:</label>
                            <input className="form_control" type="date" value={toDate} min={fromDate} onChange={(e) => setToDate(e.target.value)} />
                        </div>
                    </div>
                    <div className="mb-3">
                        <label className="label">Note (optional):</label>
                        <input
                            className="form_control"
                            type="text"
                            placeholder={type === "festival" ? "e.g. Diwali" : "e.g. Fever"}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                        />
                    </div>
                    {error && <p className="error-message d-block">{error}</p>}
                </div>

                <div className="d-flex gap-2 justify-content-end mt-3">
                    <button type="button" className="btn btn-outline-secondary" onClick={handleCloseModal} disabled={isSubmit}>Cancel</button>
                    <Button onClick={handleSubmit} disabled={isSubmit} isSubmit={isSubmit} label="Saving ..." text="Mark Leave" />
                </div>
            </div>
        </Modal>
    );
};

export default LeavePopup;
